import { ReactElement, ReactNode } from 'react';
import {
  ApiCamper,
  ApiFilter,
  ApiGeoUnit,
  ApiGeoUnitWithDayConstraints,
  ApiGetCamperDetailsResponse,
  ApiListOptions,
  ApiMultipleSelectionFilter,
  ApiSortDirection,
  ApiSortOption
} from '@ibe/api';
import { SessionStorage } from '@ibe/services';
import CamperParams from '../../SubModules/CamperDetails/CamperParams';
import isApiNumberRangeFilter, { isApiSingleSelectionFilter } from '../../../Utils/typeGuards';
import CamperStore from './CamperStore';
import { CamperSearchParams } from '../../SubModules/CamperSearch/DefaultCamperSearchSchema';
import { CollapsibleRef } from '../../SubModules/SearchCollapsible/SearchCollapsible';
import FavoritesService from '../../Services/FavoritesService';
import TravelAdviceService from '../../Services/TravelAdviceService';
import { createQueries } from '../../Hooks/useHandleQueries';
import { ExtraGroupsExternalContent, GroupName } from '../../SubModules/CamperExtras/CamperExtras';

export const sessionSearch = new SessionStorage<string>('camper-session-search');
export const sessionSearchDetails = new SessionStorage<{
  searchParams: Partial<CamperSearchParams>;
  listOptions: ApiListOptions;
}>('camper-session-search-details');

export interface ApiCamperExtended extends ApiCamper {
  tcsUrl?: string;
}

export type CamperSearchResults = {
  campers: ApiCamperExtended[];
  listOptions: ApiListOptions;
  searchParams: Partial<CamperSearchParams>;
};

export type CamperBreadcrumb = Array<{ label?: string; icon?: string; url: string }>;

export const restoreSearchDetailsFromSessionStorage = (): {
  searchParams: Partial<CamperSearchParams>;
  listOptions: ApiListOptions;
} => {
  const searchDetails = sessionSearchDetails.get();
  if (!!searchDetails) {
    return searchDetails;
  } else {
    return {
      searchParams: {},
      listOptions: {
        pagination: {
          startingFrom: 0,
          showing: 0,
          ofTotal: 0,
          currentPage: 0
        },
        sorting: {
          sortOption: ApiSortOption.PRICE,
          sortDirection: ApiSortDirection.ASC
        },
        filter: [],
        filteredCount: 0,
        count: 0
      }
    };
  }
};

const getFromGeoUnits = (
  geoUnits: ApiGeoUnitWithDayConstraints[],
  code: string
): ApiGeoUnitWithDayConstraints | undefined => {
  return geoUnits.reduce((total: ApiGeoUnit | undefined, current: ApiGeoUnitWithDayConstraints) => {
    if (!!current.children) {
      const foundEntry = Object.entries(current.children).find(entry => entry[1]?.code === code);
      return !!foundEntry ? foundEntry[1] : total;
    } else {
      return total;
    }
  }, undefined) as ApiGeoUnitWithDayConstraints;
};

const getParentFromGeoUnits = (
  geoUnits: ApiGeoUnitWithDayConstraints[],
  code: string
): ApiGeoUnitWithDayConstraints | undefined => {
  return geoUnits.find(
    geoUnit =>
      !!geoUnit.children &&
      Object.keys(geoUnit.children).includes(getFromGeoUnits(geoUnits, code || '')?.id || '')
  );
};

export const addGeoUnitsToSearchParams = (
  searchParams: Partial<CamperSearchParams>,
  geoUnits: ApiGeoUnitWithDayConstraints[]
): Partial<CamperSearchParams> => {
  return {
    ...searchParams,
    pickupStationGeoUnit:
      searchParams.pickupStationGeoUnit ||
      getFromGeoUnits(geoUnits, searchParams.pickupStation || ''),
    dropOffStationGeoUnit:
      searchParams.dropOffStationGeoUnit ||
      getFromGeoUnits(geoUnits, searchParams.dropOffStation || ''),
    pickupStationParentGeoUnit:
      searchParams.pickupStationParentGeoUnit ||
      getParentFromGeoUnits(geoUnits, searchParams.pickupStation || ''),
    dropOffStationParentGeoUnit:
      searchParams.dropOffStationParentGeoUnit ||
      getParentFromGeoUnits(geoUnits, searchParams.dropOffStation || '')
  };
};

interface CamperProps {
  onDetail?: (params: Partial<CamperParams>, listOptions?: ApiListOptions) => void;
  onSearch?: (
    params: Partial<CamperSearchParams>,
    listOptions?: ApiListOptions,
    manualSearch?: boolean
  ) => void;
  searchParams?: Partial<CamperParams>;
  listOptions?: Partial<ApiListOptions>;
  render?: (
    Search: ReactNode,
    Error: ReactNode,
    TravelAdvice: ReactNode,
    List: ReactNode,
    Detail: ReactNode,
    store: CamperStore
  ) => ReactElement;
  withModifySearch?: boolean;
  collapsibleRef?: CollapsibleRef;
  favoritesService?: FavoritesService<ApiCamperExtended>;
  onBook?: (camper: ApiCamper) => void;
  travelAdviceService: TravelAdviceService;
  externalContentTop?: ReactNode;
  externalRightSideContent?: ReactNode;
  preventInitialSearch?: boolean;
  camperSearchResults?: CamperSearchResults;
  breadcrumb?: CamperBreadcrumb;
  customSpinner?: ReactNode;
  tenant?: Tenant;
  MapReplacement?: ReactNode;
  showMapReplacement?: boolean;
  setPickupGeoUnits?: (geoUnits: ApiGeoUnitWithDayConstraints[]) => void;
  youtubeOpts?: object;
  extraGroupsExternalContent?: ExtraGroupsExternalContent;
  extraGroupsSorter?: (a: GroupName, b: GroupName) => number;
  galleryChangeTrigger?: boolean;
  footerInfos?: FooterInfos;
  filterLabelTranslationMappings?: Record<string, (label: string) => string>;
  VideoReplacement?: ReactNode;
  showVideoReplacement?: boolean;
  hideSupplierComparisonCountryCodes: string[];
  affiliateNumber?: string;
  disableResultListLazyLoading?: boolean;
}

export interface FooterInfos {
  identifierClass: string;
  footerClass: string;
}

interface CompleteSearch extends CamperParams {
  PRICE_RANGE?: string;
  CATEGORY?: string;
  BEDS_COUNT?: string;
  SERVICE_OPTIONS?: string;
  CHARACTERISTICS?: string;
  SUPPLIER?: string;
}

const filterkeys = [
  'PRICE_RANGE',
  'CATEGORY',
  'BEDS_COUNT',
  'SERVICE_OPTIONS',
  'CHARACTERISTICS',
  'SUPPLIER'
];

const queryKeyArray = [
  'productId',
  'pickupStation',
  'dropOffStation',
  'startDate',
  'endDate',
  'adults',
  'children',
  ...filterkeys
];

const createCompleteSearch = (
  searchParams: Partial<CamperParams>,
  filter: ApiFilter[]
): CompleteSearch => {
  return {
    ...searchParams,
    ...filter.reduce((total, current) => {
      return getFiltersForQueries(current)
        ? { ...total, [current.category]: getFiltersForQueries(current) }
        : { ...total };
    }, {})
  } as CompleteSearch;
};

const resetSearchParamsFilter = (
  storeParams: Record<string, string | null>
): Record<string, string | null> => {
  const params = { ...storeParams };
  filterkeys.forEach(key => {
    if (params[key]) {
      params[key] = null;
    }
  });
  return params;
};

const getFiltersForQueries = (item: ApiFilter): string | null => {
  if (isApiNumberRangeFilter(item)) {
    return item.minSelected && item.maxSelected ? `${item.minSelected},${item.maxSelected}` : null;
    // eslint-disable-next-line
  } else if (isApiSingleSelectionFilter(item)) {
    return item.selectedOption?.id || null;
  }
  const filterItem = item as ApiMultipleSelectionFilter;
  return filterItem.selectedOptions && filterItem.selectedOptions.length > 0
    ? filterItem.selectedOptions.map(option => option.id).join(',')
    : null;
};

const createFullQuery = (
  params: Record<string | number, string>,
  filter: ApiFilter[],
  window?: Window | null
) => {
  createQueries(
    createCompleteSearch(params, filter),
    [
      'productId',
      'pickupStation',
      'dropOffStation',
      'startDate',
      'endDate',
      'adults',
      'children',
      'PRICE_RANGE',
      'CATEGORY',
      'BEDS_COUNT',
      'SERVICE_OPTIONS',
      'CHARACTERISTICS',
      'SUPPLIER'
    ],
    window
  );
};

const fillFilterWithQueries = (
  filters: ApiFilter[],
  params: Record<
    keyof Omit<CompleteSearch, 'pickupStationGeoUnit' | 'dropOffStationGeoUnit'>,
    string
  >
): ApiFilter[] => {
  return filters.map(filter => {
    const category = filter.category as keyof typeof params;
    if (!params[category]) return filter;

    if (isApiNumberRangeFilter(filter)) {
      return {
        ...filter,
        minSelected: parseInt(params[category].split(',')[0], 10),
        maxSelected: parseInt(params[category].split(',')[1], 10)
      };
      // eslint-disable-next-line
    } else if (isApiSingleSelectionFilter(filter)) {
      return {
        ...filter,
        selectedOption: filter.options.find(option => option.id === params[category].split(',')[0])
      };
    }
    const filterItem = filter as ApiMultipleSelectionFilter;
    return {
      ...filterItem,
      selectedOptions:
        filterItem?.options?.filter(option => params[category].split(',').includes(option.id)) || []
    };
  });
};

const loadCampersOrDetailsWithFilters = async (
  store: CamperStore,
  params: Record<string | number, string>,
  productId?: string
): Promise<{ details: ApiGetCamperDetailsResponse | null; campers: ApiCamper[] | null }> => {
  const filters = fillFilterWithQueries(store.filter, params);
  store.setSearchParams(params);
  store.setFilters(filters);
  const newParams = {
    pickupStation: params.pickupStation,
    dropOffStation: params.dropOffStation,
    startDate: params.startDate,
    endDate: params.endDate
  };
  store.setIsLoading(true);
  let details: ApiGetCamperDetailsResponse | null = null;
  let campers: ApiCamper[] | null = null;
  if (productId) {
    details = await store.loadCamperDetail(productId, newParams);
  } else {
    campers = await store.loadCampers(newParams);
  }
  store.setIsLoading(false);
  return { details, campers };
};

const getImageUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (!url.toLowerCase().startsWith('http') && !url.toLowerCase().startsWith('data:image')) {
    return `https://${url}`;
  } else {
    return url;
  }
};

enum Tenant {
  CB = 'CB',
  MB = 'MB'
}

export {
  loadCampersOrDetailsWithFilters,
  fillFilterWithQueries,
  getFiltersForQueries,
  createCompleteSearch,
  createFullQuery,
  resetSearchParamsFilter,
  getImageUrl,
  CompleteSearch,
  CamperProps,
  queryKeyArray,
  Tenant
};
